// "From your teachers" — papers teachers uploaded + published for this board &
// grade, grouped by subject. Each paper starts through the normal exam flow
// (POST /student-papers → /exam/:spId), so the attempt ladder, hints, grading
// and the mastery sacks all behave exactly like any other paper.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';
import { useAssignedPapers } from '@/hooks/useAssignedPapers';
import { Card, Button } from '@/components/ds';
import { Icon } from '@/components/garden';
import type { TeacherPaperSummaryDTO } from '@/types';

export function TeacherPapersCard() {
  const navigate = useNavigate();
  const [startingId, setStartingId] = useState<string | null>(null);

  // The shared read: reactive claims, so it runs on a fresh load too.
  const { papers } = useAssignedPapers();

  const startExam = useMutation({
    mutationFn: (paperId: string) => apiPost<{ id: string }>('/student-papers', { paperId }),
    onSuccess: (sp) => navigate(`/exam/${sp.id}`),
    onSettled: () => setStartingId(null),
  });

  // Group by subject so a student sees "Physics — from your teacher" etc.
  // Newest first, inside a subject and across them: the paper a teacher has
  // just set is the one the student came here for, so it leads the card
  // rather than waiting its turn in the alphabet.
  const bySubject = useMemo(() => {
    const newest = (list: TeacherPaperSummaryDTO[]) =>
      Math.max(...list.map((p) => Date.parse(p.createdAt) || 0));
    const m = new Map<string, TeacherPaperSummaryDTO[]>();
    for (const p of papers) {
      const arr = m.get(p.subjectName) ?? [];
      arr.push(p);
      m.set(p.subjectName, arr);
    }
    for (const list of m.values()) {
      list.sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0));
    }
    return [...m.entries()].sort((a, b) => newest(b[1]) - newest(a[1]));
  }, [papers]);

  // Quietly absent until a teacher publishes something for this board/grade.
  if (papers.length === 0) return null;

  return (
    <Card title="From your teachers" hint="papers your teachers set for you">
      <div className="flex flex-col gap-3">
        {bySubject.map(([subjectName, subjectPapers]) => (
          <div key={subjectName}>
            <div className="mb-1.5 flex items-center gap-2">
              <span
                className="rounded-full px-2.5 py-0.5 text-fs-11 font-bold uppercase tracking-wide"
                style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
              >
                {subjectName}
              </span>
              <span className="h-px flex-1" style={{ background: 'var(--line, #e5e5e5)' }} />
            </div>

            <div className="flex flex-col gap-2">
              {subjectPapers.map((p) => {
                const minutes = Math.max(1, Math.round(p.totalSeconds / 60));
                return (
                  <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-paper px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-bold text-ink">{p.title}</span>
                        {!p.attempt && (
                          <span
                            className="shrink-0 rounded-full px-2 py-0.5 text-fs-10 font-bold uppercase tracking-wide"
                            style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
                          >
                            New
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-ink-faint" data-numeric="">
                        {p.questionCount} question{p.questionCount === 1 ? '' : 's'} · {p.totalMarks} marks · about {minutes} min
                      </div>
                    </div>

                    {p.attempt?.status === 'submitted' ? (
                      <Button size="sm" variant="secondary" onClick={() => navigate(`/reports/${p.attempt!.studentPaperId}`)}>
                        View report
                      </Button>
                    ) : p.attempt?.status === 'in_progress' ? (
                      <Button size="sm" onClick={() => navigate(`/exam/${p.attempt!.studentPaperId}`)}
                        iconLeft={<Icon name="play" size={13} />}>
                        Resume
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={startExam.isPending}
                        onClick={() => {
                          setStartingId(p.id);
                          startExam.mutate(p.id);
                        }}
                        iconLeft={<Icon name="play" size={13} />}
                      >
                        {startExam.isPending && startingId === p.id ? 'Starting…' : 'Start'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {startExam.isError && <p className="text-xs text-danger">Couldn’t start that paper. Please try again.</p>}
      </div>
    </Card>
  );
}
